/* The open project's area pages — Resourcing and Charter, and
   the not-built-yet fallback — ported VERBATIM from app.ts (renderArea ~1075,
   renderResourcing ~1934, renderRoster ~1632, renderCharter ~1838,
   renderMappings ~1149, and every helper they call).

   Why an imperatively-rendered subtree rather than JSX: these areas are the
   most focus- and caret-sensitive surfaces in the client. renderRoster repaints
   only its BODY on every month-input step (N32) so the very control the reader
   is arrow-keying through is never replaced; renderResourcing's demand editor
   and range pickers restore focus and caret SYNCHRONOUSLY right after each
   redraw. The vanilla `render(doc)` is synchronous, which is what makes those
   restorations land; React's commit is not. So ProjectAreaView hosts this
   module in a ref and lets it own its subtree exactly as the vanilla client
   did — same DOM, same classNames, same wiring, same three honest states.

   The store is frozen for the projects bundle (no new action methods), so this
   module reads and writes `store.state` and drives navigation through the
   store's existing actions. Area-internal redraws are synchronous `render()`
   calls (never a commit) so focus/caret restoration works; only leaving the
   area or changing data other React components display goes through a store
   action / `store.update` (which commits and re-runs this module from a fresh
   snapshot). Every REST call is byte-identical to app.ts. */
import { esc } from "../../esc";
import { MONTH_RE, fmtMonth, monthAdd, monthRange } from "../../../shared/months";
import { fmtFte, fmtTenths, tenths, tenthsOfPercent } from "../../../shared/fte";
import {
  ENTERED_SRC,
  needAt,
  outOfSpan,
  spanPhrase,
  validateProjectInput,
  type DemandEntry,
  type Project,
  type RoleNeed,
} from "../../../shared/projects";
import { nearMatches, normalizeName, type Role } from "../../../shared/roles";
import {
  CHARTER_FIELDS,
  CHARTER_SECTIONS,
  type CharterField,
  type CharterFieldDef,
  type CharterFieldInput,
} from "../../../shared/charter";
import type { Stamp } from "../../../shared/organizer-roles";
import type { ClientState } from "../shell/state";
import type { Store } from "../shell/store";
import { iPanel, hint } from "../shell/helpText";
import { currentSection } from "../shell/sections";
import { failureReason } from "./projectsHttp";

export interface Slots {
  banner: HTMLElement;
  sections: HTMLElement;
  footnote: HTMLElement;
}

const MAX_RANGE = 24;
const TABLE_MONTHS = 6;

/* One imperatively-managed area controller for a mounted set of slots + store.
   `render()` re-renders the whole area synchronously (the peer of the vanilla
   `render(doc)`); ProjectAreaView calls it on mount and on every React commit. */
export function createProjectArea(slots: Slots, store: Store): { render: () => void } {
  /* the live snapshot, refreshed at the top of every synchronous render pass;
     stable within a pass because no commit happens mid-render */
  let s: ClientState = store.state;

  /* ---- scoped DOM helpers (every read is scoped to #sections, PN12) ---- */
  const sec = (): HTMLElement => slots.sections;
  const pick = <T extends HTMLElement>(selector: string): T | null =>
    slots.sections.querySelector<T>(selector);
  const byId = <T extends HTMLElement>(id: string): T | null =>
    slots.sections.querySelector<T>(`#${id}`);
  const focusIn = (selector: string): void => pick<HTMLElement>(selector)?.focus();

  /* ---- help-layer wrappers (no-ops unless About-Hermes is on this surface) ---- */
  const IP = (id: Parameters<typeof iPanel>[1]): string => iPanel(s.me.surface, id);
  const H = (id: string): string => hint(s.me.surface, id);

  /* ---- shared derivations, ported verbatim ---- */
  const fmtDate = (iso: string): string =>
    new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const roleName = (roleId: string): string =>
    s.roles.find((r) => r.id === roleId)?.name ?? roleId;
  const openProject_ = (): Project | undefined =>
    s.projects.find((p) => p.id === s.openProjectId);
  const listErrorLine = (): string =>
    s.listError
      ? `<p class="form-error" role="alert" tabindex="-1">${esc(s.listError)}</p>`
      : "";

  const timeEntryLinksHtml = (): string =>
    s.timeEntryLinks
      .map((l) => `<a href="${esc(l.url)}" target="_blank" rel="noopener">${esc(l.label)}</a>`)
      .join(" · ");

  /* ---- span + charge-code value renderers (three honest states) ---- */
  function spanDateValue(label: string, date: Project["startMonth"]): string {
    return `<div class="span-date"><span class="slot-name">${label}</span>
      ${
        !date
          ? `<span class="state missing">not recorded</span>`
          : `<div class="val">${esc(fmtMonth(date.month))}</div>${
              date.unconfirmed ? `<span class="state unconfirmed">unconfirmed</span>` : ""
            }
             <div class="slot-note">${esc(date.recorded.source)}${
               date.recorded.by ? ` by ${esc(date.recorded.by.name)}` : ""
             } · effective ${fmtDate(date.recorded.date)}</div>`
      }</div>`;
  }
  function spanRow(p: Project): string {
    return `<div class="project-span">
      ${spanDateValue("Starts", p.startMonth)}
      ${spanDateValue("Ends", p.endMonth)}</div>`;
  }
  function outOfSpanSentence(p: Project, months: string[]): string {
    if (months.length === 1) return esc(outOfSpan(p, months[0] as string) as string);
    const named =
      months.length <= 4
        ? months.map(fmtMonth).join(", ")
        : `${months.length} of these months (${fmtMonth(months[0] as string)} – ${fmtMonth(months.at(-1) as string)})`;
    return `${esc(named)} fall outside this project's span — it ${esc(spanPhrase(p) as string)}`;
  }

  function chargeCodeValue(p: Project): string {
    const cc = p.chargeCode;
    return !cc
      ? `<span class="state missing">not recorded</span>
         <div class="slot-note">no charge code yet — ask the project's PM</div>`
      : `<div class="val">${esc(cc.code)}</div>${cc.unconfirmed ? `<span class="state unconfirmed">unconfirmed</span>` : ""}
         <div class="slot-note">${esc(cc.recorded.source)}${cc.recorded.by ? ` by ${esc(cc.recorded.by.name)}` : ""} · effective ${fmtDate(cc.recorded.date)}</div>`;
  }
  function chargeCodeRow(p: Project): string {
    return `<div class="charge-code">
      <span class="slot-name">Charge code (SAP)</span>
      ${chargeCodeValue(p)}
      <div class="slot-note">${timeEntryLinksHtml()}</div></div>`;
  }
  /* the permanent pointer left where the two span inputs used to be (RC15) */
  function spanPointer(named: boolean): string {
    return `<div class="slot-note span-pointer">${
      named
        ? "<b>Starts (month)</b> and <b>Ends (month)</b> are recorded on the Charter — opening it leaves this form, so save first."
        : "Change these on the Charter."
    }
      <button type="button" class="open-charter btn link small">Open the Charter</button></div>`;
  }

  /* ---- the demand editor (PN22): a searchable role list, then one role ---- */
  function matchingRoles(roles: Role[], query: string): Role[] {
    const wanted = normalizeName(query);
    if (wanted.length >= 2) return nearMatches(roles, query);
    return roles.filter((r) => normalizeName(r.name).includes(wanted));
  }
  const draftFor = (draft: readonly RoleNeed[], roleId: string): RoleNeed | undefined =>
    draft.find((n) => n.roleId === roleId);

  function roleListNote(entries: DemandEntry[], roleId: string): string {
    if (entries.length === 0)
      return `<span class="state missing">not recorded</span>
        <span class="role-note">no demand recorded</span>`;
    const month = needAt({ roleId, demand: entries }, s.currentMonth);
    const spanNote = `${entries.length} dated ${entries.length === 1 ? "entry" : "entries"}`;
    if (month === null)
      return `<span class="state missing">not recorded</span>
        <span class="role-note">nothing recorded for ${fmtMonth(s.currentMonth)} · ${spanNote}</span>`;
    /* the over-filled badge compared this against committed allocations; with
       allocations cut there is nothing to compare demand to (MVP cut) */
    return `<span class="role-note">${fmtFte(month)} FTE in ${fmtMonth(s.currentMonth)} · ${spanNote}</span>`;
  }
  /* The REST state's read-only summary: only the roles this project actually
     records demand for, in FTE for the current month. The editor's list
     (demandRoleList) shows the whole canonical catalog so a role can be
     picked; showing that at rest would read as "this project needs all of
     them". Replaces the demand-vs-assigned chart, which went with
     allocations (MVP cut, Sep 14 2026). */
  function demandSummary(p: Project): string {
    if (p.roles.length === 0)
      return `<div class="field-label roles-label">Demand by role</div>
        <p class="role-none"><span class="state missing">not recorded</span>
          No role demand is recorded for this project.</p>`;
    const rows = p.roles
      .map(
        (need) => `<li><span class="slot-main">
          <span class="slot-name">${esc(roleName(need.roleId))}</span>
          ${roleListNote(need.demand ?? [], need.roleId)}</span></li>`,
      )
      .join("");
    return `<div class="field-label roles-label">Demand by role</div>
      <ul class="slots">${rows}</ul>`;
  }

  function demandRoleList(draft: readonly RoleNeed[]): string {
    const shown = matchingRoles(s.roles, s.roleSearch);
    const ordered = [
      ...shown.filter((r) => draftFor(draft, r.id)),
      ...shown.filter((r) => !draftFor(draft, r.id)),
    ];
    const rows = ordered
      .map(
        (role) => `<li><button type="button" class="role-pick" data-role="${role.id}">
          <span class="role-pick-name">${esc(role.name)}</span>
          ${roleListNote(draftFor(draft, role.id)?.demand ?? [], role.id)}
        </button></li>`,
      )
      .join("");
    return `<div class="field-label roles-label">Demand by role</div>
      <div class="role-search-row">
        <label for="role-search">Search roles</label>
        <input id="role-search" class="role-search" type="search" value="${esc(s.roleSearch)}"
          placeholder="Type a role name" aria-describedby="role-search-count">
        <span id="role-search-count" class="role-note">${ordered.length} of ${s.roles.length}</span>
      </div>
      ${
        ordered.length
          ? `<ul class="role-list">${rows}</ul>`
          : `<p class="role-none">No role matches “${esc(s.roleSearch)}”.
               Roles come from the canonical list — add one on the Roles page.</p>`
      }`;
  }
  function demandRoleDetail(roleId: string, draft: readonly RoleNeed[]): string {
    const role = s.roles.find((r) => r.id === roleId) as Role;
    const need = draftFor(draft, roleId);
    const max = role.cardinality === "one" ? ` max="1"` : "";
    const entries = (need?.demand ?? [])
      .map(
        (entry, i) => `<div class="demand-entry" data-at="${i}">
          <label>from <input type="month" class="entry-from" value="${entry.from}"
            aria-label="from — ${esc(role.name)} entry ${i + 1}"></label>
          <label>to <small class="entry-hint">(optional)</small> <input type="month" class="entry-to" value="${entry.to ?? ""}"
            aria-label="to — ${esc(role.name)} entry ${i + 1}, empty for open-ended"></label>
          ${
            entry.to === undefined
              ? `<span class="entry-open">open-ended</span>`
              : ""
          }
          <label>needs <input type="number" class="entry-count" min="0" step="0.1"${max}
            value="${Number.isFinite(entry.count) ? entry.count : ""}"
            aria-label="FTE — ${esc(role.name)} entry ${i + 1}"></label>
          <span class="entry-unit">FTE</span>
          <button type="button" class="drop-entry btn ghost small" data-at="${i}"
            aria-label="Remove ${esc(role.name)} entry ${i + 1}">Remove</button>
        </div>`,
      )
      .join("");
    return `<div class="demand-detail">
      <button type="button" class="back-roles btn ghost small">← All roles</button>
      <div class="field-label roles-label">${esc(role.name)} — demand by date range</div>
      ${
        entries ||
        `<p class="role-none"><span class="state missing">not recorded</span>
           This project records no demand for ${esc(role.name)} yet.</p>`
      }
      <button type="button" class="add-entry btn ghost small">Add a demand entry</button>
    </div>`;
  }
  /* read the OPEN role's entries back out of the DOM and merge into the draft */
  function commitOpenRole(): void {
    const roleId = s.demandRole;
    const draft = s.roleDraft;
    if (roleId === null || draft === null) return;
    const panel = sec();
    const demand: DemandEntry[] = [
      ...panel.querySelectorAll<HTMLElement>(".demand-entry"),
    ].map((row) => {
      const value = (selector: string): string =>
        (row.querySelector(selector) as HTMLInputElement).value;
      const to = value(".entry-to");
      const raw = value(".entry-count");
      return {
        from: value(".entry-from"),
        ...(to ? { to } : {}),
        count: raw === "" ? NaN : Number(raw),
      };
    });
    const others = draft.filter((n) => n.roleId !== roleId);
    const next = demand.length ? [...others, { roleId, demand }] : others;
    s.roleDraft = s.roles
      .map((r) => next.find((n) => n.roleId === r.id))
      .filter((n): n is RoleNeed => n !== undefined);
  }
  function wireRoleList(): void {
    const search = pick<HTMLInputElement>(".role-search") as HTMLInputElement;
    search.addEventListener("input", () => {
      const at = search.selectionStart as number;
      s.roleSearch = search.value;
      render();
      const again = pick<HTMLInputElement>(".role-search") as HTMLInputElement;
      again.focus();
      again.setSelectionRange(at, at);
    });
    const picks = [...sec().querySelectorAll<HTMLElement>(".role-pick")];
    search.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "ArrowDown") {
        e.preventDefault();
        picks[0]?.focus();
      }
    });
    picks.forEach((p, i) => {
      p.addEventListener("click", () => {
        s.demandRole = p.dataset.role as string;
        render();
        focusIn(".demand-entry .entry-from, .add-entry");
      });
      p.addEventListener("keydown", (e) => {
        const key = (e as KeyboardEvent).key;
        if (key !== "ArrowDown" && key !== "ArrowUp") return;
        e.preventDefault();
        if (key === "ArrowUp" && i === 0) search.focus();
        else picks[key === "ArrowDown" ? i + 1 : i - 1]?.focus();
      });
    });
  }
  function wireRoleDetail(roleId: string, project: Project): void {
    const panel = sec();
    panel.querySelector(".back-roles")!.addEventListener("click", () => {
      commitOpenRole();
      s.demandRole = null;
      render();
      focusIn(`.role-pick[data-role="${roleId}"]`);
    });
    panel.querySelector(".add-entry")!.addEventListener("click", () => {
      commitOpenRole();
      const draft = s.roleDraft as RoleNeed[];
      const existing = draftFor(draft, roleId)?.demand ?? [];
      const previous = existing.at(-1);
      const from = previous
        ? monthAdd(previous.to ?? previous.from, 1)
        : (project.startMonth?.month ?? s.currentMonth);
      const entry: DemandEntry = { from, count: previous?.count ?? NaN };
      const others = draft.filter((n) => n.roleId !== roleId);
      s.roleDraft = s.roles
        .map((r) =>
          r.id === roleId
            ? { roleId, demand: [...existing, entry] }
            : others.find((n) => n.roleId === r.id),
        )
        .filter((n): n is RoleNeed => n !== undefined);
      render();
      focusIn(`.demand-entry[data-at="${existing.length}"] .entry-from`);
    });
    for (const drop of panel.querySelectorAll<HTMLElement>(".drop-entry"))
      drop.addEventListener("click", () => {
        commitOpenRole();
        const draft = s.roleDraft as RoleNeed[];
        const at = Number(drop.dataset.at);
        const kept = ((draftFor(draft, roleId) as RoleNeed).demand as DemandEntry[]).filter(
          (_e, i) => i !== at,
        );
        const others = draft.filter((n) => n.roleId !== roleId);
        s.roleDraft = s.roles
          .map((r) =>
            r.id === roleId && kept.length
              ? { roleId, demand: kept }
              : others.find((n) => n.roleId === r.id),
          )
          .filter((n): n is RoleNeed => n !== undefined);
        render();
        focusIn(".add-entry");
      });
  }

  /* the one Charter pointer wiring — opening the project's Charter (RC15) */
  function wireCharterPointer(id: string): void {
    for (const btn of sec().querySelectorAll<HTMLElement>(".open-charter"))
      btn.addEventListener("click", () => {
        store.openProject(id);
        store.openArea("Charter");
      });
  }

  /* ---- Resourcing ---- */
  async function saveRoles(p: Project): Promise<void> {
    if (s.saving) return;
    commitOpenRole();
    const roles = (s.roleDraft as RoleNeed[]).filter(
      (n) => (n.demand as DemandEntry[]).length > 0,
    );
    const fault = validateProjectInput({ ...p, roles }, s.roles, p);
    if (fault) {
      s.roleDraft = roles;
      s.listError = fault;
      render();
      const alert = pick<HTMLElement>(".form-error");
      alert?.scrollIntoView({ block: "center" });
      alert?.focus();
      return;
    }
    s.saving = true;
    s.listError = null;
    try {
      const res = await fetch(`/api/projects/${p.id}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: p.name,
          roles,
          chargeCode: p.chargeCode
            ? { code: p.chargeCode.code, unconfirmed: p.chargeCode.unconfirmed }
            : null,
          directoryId: p.directoryId,
        }),
      });
      if (!res.ok) {
        s.saving = false;
        s.listError = await failureReason(res, "Couldn't record the roles — is the server healthy?");
        return render();
      }
      const saved = (await res.json()) as Project;
      s.saving = false;
      /* projects changed and the editor closes — commit so the drawer/navigator
         refresh too, then this module re-renders from the fresh snapshot */
      store.update({
        projects: s.projects.map((x) => (x.id === saved.id ? saved : x)),
        roleDraft: null,
        demandRole: null,
        listError: null,
      });
    } catch {
      s.saving = false;
      s.listError = "Couldn't record the roles — is the server healthy?";
      render();
    }
  }

  function renderResourcing(p: Project): void {
    const recorded = p.roles
      .map((r) => needAt(r, s.currentMonth))
      .filter((v): v is number => v !== null);
    const needs = recorded.reduce((n, v) => n + tenths(v), 0);
    const silent = p.roles.length - recorded.length;
    slots.banner.innerHTML = `<div class="banner">
      <b>Resourcing — ${esc(p.name)}.</b>
      <span>${
        recorded.length
          ? `${fmtTenths(needs)} FTE of role demand in ${fmtMonth(s.currentMonth)}${
              silent
                ? ` <span class="state missing">not recorded</span> for ${silent} more role${silent === 1 ? "" : "s"} this month`
                : ""
            }.`
          : "No demand recorded for this month — use <b>Edit demand</b> below to record what this project needs, and when."
      }</span></div>`;
    const draft = s.roleDraft;
    if (draft) {
      const open = s.demandRole;
      const body = open === null ? demandRoleList(draft) : demandRoleDetail(open, draft);
      slots.sections.innerHTML = `${listErrorLine()}<section class="area-panel">
        ${IP("fte-and-demand")}
        ${body}
        <div class="form-actions">
          <button class="save-roles btn primary">Save demand</button>
          <button class="cancel-roles btn ghost">Cancel</button>
        </div></section>`;
      slots.footnote.innerHTML =
        open === null
          ? "Roles come from the canonical list — never free text. Pick one to record when the project needs it, and how many."
          : "Every demand entry carries a date range. Leave <b>to</b> empty for an open-ended need — never invent an end date.";
      pick(".save-roles")!.addEventListener("click", () => void saveRoles(p));
      pick(".cancel-roles")!.addEventListener("click", () => {
        s.roleDraft = null;
        s.demandRole = null;
        s.listError = null;
        render();
      });
      if (open === null) wireRoleList();
      else wireRoleDetail(open, p);
      return;
    }
    slots.sections.innerHTML = `${listErrorLine()}<section class="area-panel">
      ${spanRow(p)}${spanPointer(false)}
      ${demandSummary(p)}
      ${IP("fte-and-demand")}
      ${chargeCodeRow(p)}
      <div class="form-actions"><button class="edit-roles btn ghost small">Edit demand</button></div>
      </section>`;
    slots.footnote.innerHTML =
      "Demand is what the project needs, by month. Who fills it is not recorded in Hermes.";
    pick(".edit-roles")!.addEventListener("click", () => {
      s.demandRole = null;
      s.roleSearch = "";
      s.roleDraft = p.roles.map((n) => ({
        roleId: n.roleId,
        demand: (n.demand ?? [
          { from: p.startMonth?.month ?? s.currentMonth, count: n.count as number },
        ]).map((e) => ({ ...e })),
      }));
      render();
    });
    wireCharterPointer(p.id);
  }

  /* ---- Charter (designs/charter-form.md CH8/CH9/CH12) ---- */
  const stampLine = (recorded: Stamp): string =>
    `${esc(recorded.source)}${recorded.by ? ` by ${esc(recorded.by.name)}` : ""} · effective ${fmtDate(recorded.date)}`;
  const stampInner = (value: { recorded: Stamp } | null): string =>
    !value
      ? `<span class="state missing">not recorded</span>`
      : `<div class="slot-note">${stampLine(value.recorded)}</div>`;
  const charterStamp = (value: { recorded: Stamp } | null): string =>
    `<div class="charter-stamp">${stampInner(value)}</div>`;
  function charterDateField(
    which: "start" | "end",
    label: string,
    date: Project["startMonth"],
  ): string {
    return `<div class="charter-date">
      <label class="field"><span class="field-label">${label}</span>
        <input id="${which}-month" type="month" value="${esc(date?.month ?? "")}"></label>
      ${charterStamp(date)}
      <label class="charter-unconfirmed-row"><input id="${which}-month-unconfirmed" type="checkbox"
        ${date?.unconfirmed ? "checked" : ""}>
        <span>Unconfirmed — an assumption, not verified yet</span></label></div>`;
  }
  const spanFromForm = (which: "start" | "end") => {
    const month = (byId<HTMLInputElement>(`${which}-month`) as HTMLInputElement).value.trim();
    return month
      ? {
          month,
          unconfirmed: (byId<HTMLInputElement>(`${which}-month-unconfirmed`) as HTMLInputElement)
            .checked,
        }
      : null;
  };
  /* one field at rest: label · value (pre-wrap) or red · orange · stamp */
  function charterFieldValue(def: CharterFieldDef, f: CharterField | null): string {
    return `<div class="charter-field" data-field="${def.id}">
      <span class="slot-name">${esc(def.label)}</span>
      ${
        !f
          ? `<span class="state missing">not recorded</span>`
          : `<div class="val charter-text">${esc(f.value)}</div>${
              f.unconfirmed ? `<span class="state unconfirmed">unconfirmed</span>` : ""
            }<div class="slot-note">${stampLine(f.recorded)}</div>`
      }</div>`;
  }
  /* the same field as an input, with its recorded stamp and its own
     Unconfirmed box; the stamp slot doubles as the live "will record as" hint */
  function charterFieldInput(def: CharterFieldDef, f: CharterField | null): string {
    const control =
      def.kind === "choice"
        ? `<select id="charter-${def.id}">
            <option value="">— not recorded —</option>
            ${(def.options ?? [])
              .map((o) => `<option value="${esc(o)}" ${f?.value === o ? "selected" : ""}>${esc(o)}</option>`)
              .join("")}</select>`
        : `<textarea id="charter-${def.id}" rows="3">${esc(f?.value ?? "")}</textarea>`;
    return `<div class="charter-field editing" data-field="${def.id}">
      <label class="field"><span class="field-label">${esc(def.label)}</span>${control}</label>
      ${def.hint ? `<p class="field-hint">${esc(def.hint)}</p>` : ""}
      ${charterStamp(f)}
      <label class="charter-unconfirmed-row"><input id="charter-${def.id}-unconfirmed" type="checkbox"
        ${f?.unconfirmed ? "checked" : ""}>
        <span>Unconfirmed — an assumption, not verified yet</span></label></div>`;
  }
  const charterElsewhere = (): string => `<div class="charter-elsewhere">
      <span class="state missing">recorded elsewhere or not yet</span>
      <ul class="slot-note">
        <li>Project manager, sponsor and SME, project team, support resources, process owner — <b>Roles &amp; RACI</b> (not built yet)</li>
        <li>Key milestones — <b>Deliverables</b> (not built yet)</li>
        <li>Expected savings, estimated costs, the cost table, benefit amounts, approval thresholds — deferred until permissions are finer-grained</li>
      </ul></div>`;
  const charterSections = (p: Project, editing: boolean): string =>
    CHARTER_SECTIONS.map(
      (section, i) => `<section class="area-panel charter-section" aria-labelledby="charter-h-${i}">
        <h3 id="charter-h-${i}">${esc(section.title)}</h3>
        ${
          i === 0
            ? editing
              ? `<p class="field-hint charter-hint">Leave a month blank if it is not known.</p>
                 <div class="project-span charter-span">
                   ${charterDateField("start", "Starts", p.startMonth)}
                   ${charterDateField("end", "Ends", p.endMonth)}</div>`
              : spanRow(p)
            : ""
        }
        ${section.fields
          .map((def) =>
            editing ? charterFieldInput(def, p.charter[def.id]) : charterFieldValue(def, p.charter[def.id]),
          )
          .join("")}</section>`,
    ).join("");
  const sourceInput = (): string =>
    `<label class="field charter-source-field"><span class="field-label">Source of these values</span>
      <input id="charter-source" type="text" placeholder="e.g. Scythe Project Charter, V. Davis, Jul 27 2026">
      <span class="field-hint">Blank means Entered in app. Applied to every field you change in this save.</span></label>`;
  const fieldFromForm = (def: CharterFieldDef, source: string): CharterFieldInput | null => {
    const value = (byId<HTMLInputElement>(`charter-${def.id}`) as HTMLInputElement).value.trim();
    if (!value) return null;
    const unconfirmed = (byId<HTMLInputElement>(`charter-${def.id}-unconfirmed`) as HTMLInputElement).checked;
    return { value, unconfirmed, ...(source ? { source } : {}) };
  };
  /* does the form's value for this field differ from the record it opened on? */
  const fieldChanged = (p: Project, def: CharterFieldDef, supplied: CharterFieldInput | null): boolean => {
    const recorded = p.charter[def.id];
    return supplied
      ? !recorded || recorded.value !== supplied.value || recorded.unconfirmed !== supplied.unconfirmed
      : !!recorded;
  };
  /* the live hint (UI review): a field whose value or flag differs from the
     record shows what its stamp WILL say; an untouched field keeps its stamp */
  function refreshWillRecord(p: Project, def: CharterFieldDef): void {
    const slot = pick<HTMLElement>(`.charter-field[data-field="${def.id}"] .charter-stamp`);
    if (!slot) return;
    const source = (byId<HTMLInputElement>("charter-source") as HTMLInputElement).value.trim();
    const supplied = fieldFromForm(def, source);
    const recorded = p.charter[def.id];
    slot.innerHTML = fieldChanged(p, def, supplied)
      ? `<div class="slot-note charter-will-record">will record as: ${esc(source || ENTERED_SRC)}</div>`
      : stampInner(recorded);
  }
  async function saveCharter(p: Project): Promise<void> {
    if (s.saving) return;
    const source = (byId<HTMLInputElement>("charter-source") as HTMLInputElement).value.trim();
    /* ONLY what this form changed travels (CH8): an untouched field is
       absent, which the server reads as KEEP — so a save never restamps a
       field the reader did not touch, even if someone else saved the record
       while the form was open (Copilot review of PR #120). */
    const dirtyDate = (which: "start" | "end") => {
      const supplied = spanFromForm(which);
      const recorded = which === "start" ? p.startMonth : p.endMonth;
      const same = supplied
        ? !!recorded && recorded.month === supplied.month && recorded.unconfirmed === supplied.unconfirmed
        : !recorded;
      return same ? undefined : supplied;
    };
    const dirtyFields = CHARTER_FIELDS.flatMap((def) => {
      const supplied = fieldFromForm(def, source);
      return fieldChanged(p, def, supplied) ? [[def.id, supplied] as const] : [];
    });
    const input = {
      name: p.name,
      roles: p.roles,
      chargeCode: p.chargeCode
        ? { code: p.chargeCode.code, unconfirmed: p.chargeCode.unconfirmed }
        : null,
      directoryId: p.directoryId,
      startMonth: dirtyDate("start"),
      endMonth: dirtyDate("end"),
      charter: Object.fromEntries(dirtyFields),
    };
    const danglingDate = (["start", "end"] as const).find(
      (which) =>
        !(byId<HTMLInputElement>(`${which}-month`) as HTMLInputElement).value.trim() &&
        (byId<HTMLInputElement>(`${which}-month-unconfirmed`) as HTMLInputElement).checked,
    );
    const danglingField = CHARTER_FIELDS.find(
      (def) =>
        !(byId<HTMLInputElement>(`charter-${def.id}`) as HTMLInputElement).value.trim() &&
        (byId<HTMLInputElement>(`charter-${def.id}-unconfirmed`) as HTMLInputElement).checked,
    );
    const reason = danglingDate
      ? `Enter the ${danglingDate === "start" ? "start" : "end"} month, or clear its Unconfirmed box — an assumption needs a value to be about.`
      : danglingField
        ? `Enter a value for ${danglingField.label}, or clear its Unconfirmed box — an assumption needs a value to be about.`
        : validateProjectInput(input, s.roles, p);
    if (reason) {
      s.charterError = reason;
      /* a stale list error would otherwise render its own .form-error above
         this one and take the focus meant for the reason (review, Sep 16 2026) */
      s.listError = null;
      render();
      pick<HTMLElement>(".form-error")?.focus();
      return;
    }
    s.saving = true;
    s.charterError = null;
    s.listError = null;
    const oops = "Couldn't record the charter — is the server healthy?";
    try {
      const res = await fetch(`/api/projects/${p.id}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        s.saving = false;
        s.listError = await failureReason(res, oops);
        return render();
      }
      const saved = (await res.json()) as Project;
      s.saving = false;
      s.charterEditing = false;
      charterFocus = "edit";
      /* commit so the navigator's record stamp refreshes, then re-render */
      store.update({
        charterEditing: false,
        projects: s.projects.map((x) => (x.id === saved.id ? saved : x)),
      });
    } catch {
      s.saving = false;
      s.listError = oops;
      render();
    }
  }
  /* where focus lands after the next Charter render: the Source input on
     entering edit, the Edit button on leaving it (UI review, Sep 16 2026) */
  let charterFocus: "source" | "edit" | null = null;
  function renderCharter(p: Project): void {
    slots.banner.innerHTML = `<div class="banner">
      <b>Charter — ${esc(p.name)}.</b></div>`;
    const editing = s.charterEditing;
    slots.sections.innerHTML = `${listErrorLine()}
      ${
        editing
          ? `<section class="area-panel charter-form-head">${sourceInput()}</section>`
          : `<div class="form-actions charter-actions"><button class="edit-charter btn ghost small">Edit charter</button></div>`
      }
      ${charterSections(p, editing)}
      ${IP("stamps-and-honesty")}
      ${
        editing
          ? `<p class="form-error" tabindex="-1" role="alert">${esc(s.charterError ?? "")}</p>
             <div class="form-actions">
               <button class="save-charter btn primary">Save charter</button>
               <button class="cancel-charter btn ghost">Cancel</button>
             </div>`
          : ""
      }
      ${charterElsewhere()}`;
    slots.footnote.innerHTML =
      "A charter field is a stamped, effective-dated revision — changing one records a new value, it never overwrites the old. The history itself is not shown in the app yet.";
    if (editing) {
      pick(".save-charter")!.addEventListener("click", () => void saveCharter(p));
      pick(".cancel-charter")!.addEventListener("click", () => {
        s.charterError = null;
        s.listError = null;
        s.charterEditing = false;
        charterFocus = "edit";
        render();
      });
      const refreshAll = (): void => CHARTER_FIELDS.forEach((def) => refreshWillRecord(p, def));
      byId<HTMLInputElement>("charter-source")?.addEventListener("input", refreshAll);
      for (const def of CHARTER_FIELDS) {
        byId<HTMLElement>(`charter-${def.id}`)?.addEventListener("input", () => refreshWillRecord(p, def));
        byId<HTMLElement>(`charter-${def.id}`)?.addEventListener("change", () => refreshWillRecord(p, def));
        byId<HTMLElement>(`charter-${def.id}-unconfirmed`)?.addEventListener("change", () =>
          refreshWillRecord(p, def),
        );
      }
    } else {
      pick(".edit-charter")!.addEventListener("click", () => {
        s.charterError = null;
        s.charterEditing = true;
        charterFocus = "source";
        render();
      });
    }
    if (charterFocus === "source") byId<HTMLInputElement>("charter-source")?.focus();
    if (charterFocus === "edit") pick<HTMLElement>(".edit-charter")?.focus();
    charterFocus = null;
  }

  /* ---- the area dispatcher, ported from renderArea (app.ts ~1075) ---- */
  function renderArea(): void {
    const p = openProject_();
    if (!p) {
      slots.banner.innerHTML = `<div class="banner">
        <b>${esc(s.area as string)}.</b>
        <span>The project this was about is no longer in the record.</span></div>`;
      slots.sections.innerHTML = `<div class="area-note">
        <p>That project has been deleted. Use <b>File → View</b> to open another.</p></div>`;
      slots.footnote.innerHTML = "";
      return;
    }
    const tab = currentSection(store)!.tabs.find((x) => x.label === s.area)!;
    if (tab.label === "Resourcing") return renderResourcing(p);
    if (tab.label === "Charter") return renderCharter(p);
    slots.banner.innerHTML = `<div class="banner">
      <b>${esc(tab.label)} — ${esc(p.name)}.</b>
      <span>An area of the project record — not recorded in the app yet.</span></div>`;
    slots.sections.innerHTML = `<div class="area-note">
      <p>${esc(tab.note)}</p>
      <p>Nothing is recorded here for <b>${esc(p.name)}</b>.</p></div>`;
    slots.footnote.innerHTML =
      "<b>Red = not recorded.</b> A named area with nothing behind it is a gap the app is honest about, not a page that failed to load.";
  }

  function render(): void {
    s = store.state;
    renderArea();
  }

  return { render };
}
